/**
 * Stream B — B37: Content Calendar
 *
 * Weekly grid for social posts. AI-suggested slots.
 * Real apiFetch. No mocks. CSS variables only.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { formatMonthDay } from "@/lib/i18n";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Plus,
  AlertCircle,
  CheckCircle2,
  XCircle,
} from "lucide-react";

interface CalendarSlot {
  id: string;
  date: string;
  time: string;
  platform: string;
  title: string;
  content: string;
  status: "draft" | "scheduled" | "published" | "failed";
  isAiSuggested: boolean;
}

interface ContentCalendarData {
  slots: CalendarSlot[];
  weekStart: string;
  weekEnd: string;
  platformAccounts: Array<{ platform: string; accountName: string; connected: boolean }>;
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-foreground)]",
  scheduled: "bg-[var(--color-primary)]/5 border-[var(--color-primary)]/30 text-[var(--color-primary)]",
  published: "bg-[var(--color-success)]/5 border-[var(--color-success)]/30 text-[var(--color-success)]",
  failed: "bg-[var(--color-error)]/5 border-[var(--color-error)]/30 text-[var(--color-error)]",
};

function SlotCard({ slot }: { slot: CalendarSlot }) {
  const statusStyle = STATUS_STYLES[slot.status] ?? STATUS_STYLES.draft;
  return (
    <div className={`rounded border p-1.5 text-xs ${statusStyle} ${slot.isAiSuggested ? "ring-1 ring-[var(--color-ai)]/40" : ""}`}>
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-[var(--color-muted-foreground)]">{slot.platform}</span>
        {slot.isAiSuggested && <Sparkles className="h-3 w-3 text-[var(--color-ai)]" />}
        <span className="truncate font-medium">{slot.title}</span>
      </div>
      <div className="mt-0.5 text-[10px] opacity-70">{slot.time}</div>
    </div>
  );
}

function getWeekStart(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split("T")[0] ?? "";
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0] ?? "";
}

function formatDateRange(start: string): string {
  const end = addDays(start, 6);
  return `${formatMonthDay(start)} — ${formatMonthDay(end)}`;
}

export default function ContentCalendar() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [data, setData] = useState<ContentCalendarData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const currentWeekStart = useMemo(() => {
    const base = new Date();
    base.setDate(base.getDate() + weekOffset * 7);
    return getWeekStart(base);
  }, [weekOffset]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiFetch<{ data: ContentCalendarData }>(`/crm/social/calendar?week=${currentWeekStart}`);
      setData(res.data);
      setError(false);
    } catch {
      setData(null);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [currentWeekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const slotsByDay = useMemo(() => {
    const map = new Map<string, CalendarSlot[]>();
    for (let i = 0; i < 7; i++) map.set(addDays(currentWeekStart, i), []);
    if (data?.slots) {
      for (const slot of data.slots) {
        const day = slot.date.split("T")[0] ?? "";
        const existing = map.get(day);
        if (existing) existing.push(slot);
      }
    }
    return map;
  }, [data, currentWeekStart]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 animate-pulse rounded bg-[var(--color-surface)]" />
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 42 }, (_, i) => (
            <div key={i} className="h-24 animate-pulse rounded bg-[var(--color-surface)]" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-4 text-sm text-[var(--color-error)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load content calendar.
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
        <Calendar className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" />
        <p className="font-medium text-[var(--color-foreground)]">No content scheduled</p>
        <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Connect your social accounts and start planning content.</p>
      </div>
    );
  }

  const dayDates = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  const today = new Date().toISOString().split("T")[0] ?? "";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Content Calendar</h1>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setWeekOffset((w) => w - 1)} className="rounded-lg border border-[var(--color-border)] p-1.5 hover:bg-[var(--color-surface)]">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium text-[var(--color-foreground)] min-w-[180px] text-center">
            {formatDateRange(currentWeekStart)}
          </span>
          <button type="button" onClick={() => setWeekOffset((w) => w + 1)} className="rounded-lg border border-[var(--color-border)] p-1.5 hover:bg-[var(--color-surface)]">
            <ChevronRight className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setWeekOffset(0)} className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium hover:bg-[var(--color-surface)]">
            Today
          </button>
        </div>
      </div>

      {data.platformAccounts.length > 0 && (
        <div className="flex items-center gap-3 text-xs text-[var(--color-muted-foreground)]">
          {data.platformAccounts.map((acc) => (
            <span key={acc.platform} className="flex items-center gap-1">
              {acc.platform}
              {acc.connected ? (
                <CheckCircle2 className="h-3 w-3 text-[var(--color-success)]" />
              ) : (
                <XCircle className="h-3 w-3 text-[var(--color-error)]" />
              )}
              {acc.accountName}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-7 gap-2">
        {DAYS.map((day, i) => {
          const dateStr = dayDates[i] ?? "";
          const isToday = dateStr === today;
          return (
            <div key={day} className={`rounded-lg p-2 text-center text-sm font-medium ${isToday ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]" : "text-[var(--color-muted-foreground)]"}`}>
              {day} {new Date(dateStr).getDate()}
            </div>
          );
        })}
        {dayDates.map((dateStr) => {
          const slots = slotsByDay.get(dateStr) ?? [];
          const isToday = dateStr === today;
          return (
            <div key={dateStr} className={`min-h-[120px] rounded-lg border p-2 ${isToday ? "border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5" : "border-[var(--color-border)] bg-[var(--color-background)]"}`}>
              <div className="space-y-1.5">
                {[...slots].sort((a, b) => a.time.localeCompare(b.time)).map((slot) => (
                  <SlotCard key={slot.id} slot={slot} />
                ))}
              </div>
              {slots.length === 0 && (
                <button type="button" className="flex w-full items-center justify-center gap-1 rounded border border-dashed border-[var(--color-border)] py-3 text-xs text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/30 hover:text-[var(--color-primary)]">
                  <Plus className="h-3 w-3" /> Add post
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
